define(
  'builderShowcaseHelper',[
    'builderShowcaseObserver',
    'fastdom',
  ],
  (
    builderShowcaseObserver,
    fastdom,
  ) => {
    'use strict';

    /**
    * @private
    * setUpTextValueInDomElement: assign a text value to a dom element innerText prop
    */
    function setUpTextValueInDomElement(domObjects, text) {
      const elements = domObjects;
      for (let i = 0; i < elements.length; i += 1) {
        elements[i].innerText = text;
      }
    }

    /**
    * @private
    * setBodyModalInformation: sets the modal body content, it could be a version for brands where
     * all of their plans are part of a plan library, or another version where this statement is false.
     * Check BDXNHS-7903 for information about version 1
     * Check BDXNHS-8058 for information about version 2
    */
    function setBodyModalInformation(info, container) {
      const locationsContainer = container.querySelector('[data-component="locations-container"]');
      const locations = container.querySelector('[data-component="locations-text"]');
      const locationsLink = container.querySelector('[data-component="locations-link"]');
      const planLibraryContainer = container.querySelector('[data-component="plan-library-container"]');
      const planLibraryStateTemplate = document.querySelector('[data-plan-library-state]');
      const planLibraryMarketTemplate = document.querySelector('[data-plan-library-market]');
      const planLibraryCommunityTemplate = document.querySelector('[data-plan-library-community]');

      if (info.jsonResponse.IsManufactured) {
        return;
      }

      if (info.jsonResponse.PlanLibraryHomes && info.jsonResponse.PlanLibraryHomes.length > 0) {
        planLibraryContainer.removeAttribute('hidden');
        locationsContainer.setAttribute('hidden', '');

        for (let i = 0; i < info.jsonResponse.PlanLibraryHomes.length; i += 1) {
          const stateListItem = planLibraryStateTemplate.content.cloneNode(true);
          const state = info.jsonResponse.PlanLibraryHomes[i];
          stateListItem.querySelector('[data-role-state]').innerHTML = state.State.StateName;

          for (let j = 0; j < info.jsonResponse.PlanLibraryHomes[i].Markets.length; j += 1) {
            const market = info.jsonResponse.PlanLibraryHomes[i].Markets[j];
            const marketListItem = planLibraryMarketTemplate.content.cloneNode(true);
            marketListItem.querySelector('[data-role-market]').innerHTML = market.Market.Name;

            for (let k = 0; k < info.jsonResponse.PlanLibraryHomes[i].Markets[j].Communities.length; k += 1) {
              const comm = info.jsonResponse.PlanLibraryHomes[i].Markets[j].Communities[k];
              const communityListItem = planLibraryCommunityTemplate.content.cloneNode(true);
              const anchorTag = communityListItem.querySelector('a');
              anchorTag.innerHTML = comm.Community.Name;
              anchorTag.href = comm.CommunityUrl;
              marketListItem.querySelector('[data-role-comm-list]').appendChild(communityListItem);
            }

            stateListItem.querySelector('[data-role-market-list]').appendChild(marketListItem);
          }

          planLibraryContainer.querySelector('ul').appendChild(stateListItem);
        }

        return;
      }

      if (locations) {
        locationsContainer.removeAttribute('hidden');
        locations.innerText = locations.innerText.replace('[BUILDERNAME]', info.jsonResponse.BrandName);
        locations.innerText = locations.innerText.replace('[METROCOUNT]', info.jsonResponse.MetroAreasCount);
        locationsLink.href = info.jsonResponse.LocationsLink;
      }
    }

    /**
    * @private
    * setUpPlanDescription: creates the text to be hold by the description section.
    */
    function setUpPlanDescription(info, container) {
      if (!info || (!info.PlanName && (!info.selectedItem || !info.selectedItem.PlanName))) {
        return;
      }

      const planName = info.PlanName || info.selectedItem.PlanName;
      const planAmenties = info.Amenities || info.selectedItem.Amenities;
      const planNameElements = container.querySelectorAll('[data-component="plan-name"]');
      const planAmmenitiesElements = container.querySelectorAll('[data-component="plan-ammenities"]');
      const builderName = container.querySelector('[data-component="builder-name"]');
      const builderLink = container.querySelector('[data-component="builder-link"]');
      const reviewsStars = container.querySelector('[data-component="reviews-starSummary"]');
      const reviewsScoreText = container.querySelector('[data-component="reviews-scoreText"]');
      const reviewsInformation = container.querySelector('[data-component="reviews-information"]');

      if (!planName) {
        setUpTextValueInDomElement(planNameElements, '');
        setUpTextValueInDomElement(planAmmenitiesElements, '');
        return;
      }

      setUpTextValueInDomElement(planNameElements, planName);
      setUpTextValueInDomElement(planAmmenitiesElements, planAmenties.join(';').replace(/;/g, ' | '));

      if (!info.jsonResponse) {
        return;
      }

      if (builderName) {
        builderName.innerText = builderName.innerText.replace('[BUILDERNAME]', info.jsonResponse.BrandName);
      }

      if (builderLink) {
        builderLink.href = info.data.BuilderLink;
      }

      if (info.data.hasBuilderReviews && reviewsStars && reviewsScoreText) {
        const ratingValue = info.data.averageRating;
        reviewsStars.style = `--rating:${ratingValue}`;
        reviewsInformation.removeAttribute('hidden');
      }

      setBodyModalInformation(info, container);
    }

    /**
    * @private
    * Gets the image type for the source
    */
    function getImageType(url) {
      const sections = url.split(',');
      const source = sections[0].split(' ');
      const address = source[0];
      let type = 'image/';

      if (address.indexOf('webp') > 0) {
        type += 'webp';
      } else if (address.indexOf('avif') > 0) {
        type += 'avif';
      } else {
        type += 'jpeg';
      }

      return type;
    }

    /**
    * @private
    * degreeToRadius: converts degrees to radius
    *
    */
    function degreeToRadius(degree) {
      return degree * (Math.PI / 180);
    }

    /**
    * @private
    * calculateHaversineFormula: determines the great-circle distance between two points
    * on a sphere given their longitudes and latitudes. For more:
    * https://es.wikipedia.org/wiki/F%C3%B3rmula_del_semiverseno
    *
    */
    function calculateHaversineFormula(latitude, longitude, destinationLatitude, destinationLongitude) {
      const earthRadius = 6371; // Radius of the earth in km
      const distanceLatitude = degreeToRadius(destinationLatitude - latitude);
      const distanceLongitude = degreeToRadius(destinationLongitude - longitude);
      const a =
        Math.sin(distanceLatitude / 2) * Math.sin(distanceLatitude / 2) +
          Math.cos(degreeToRadius(latitude)) * Math.cos(degreeToRadius(destinationLatitude)) *
          Math.sin(distanceLongitude / 2) * Math.sin(distanceLongitude / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return earthRadius * c; // Distance in km
    }

    function onMapUpdatedSuccess(html, config) {
      const configObject = config;

      if (!document.querySelector('[data-ismobiledevice="true"]')) {
        configObject.infowindow.setContent(html);
        configObject.infowindow.open({ anchor: configObject.marker, map: configObject.mapInstance });
        if (configObject.marker.content) {
          configObject.marker.content.style.cursor = 'pointer';
        }
        require(['mapCardCtaHandler'], (mapCardCtaHandler) => {
          mapCardCtaHandler.setMapInstance(configObject.mapItem);
        });

        return;
      }

      configObject.cardsHtmlContainer.innerHTML = html;
      configObject.cardsHtmlContainer.style.opacity = '1';
      configObject.cardsHtmlContainer.removeAttribute('hidden');
      require(
        ['loadCss'],
        (loadCss) => {
          const cardCssInput = document.querySelector('#card-css');
          loadCss(cardCssInput.value);
        }
      );
    }

    /**
    * Gets from the state marquee gallary collection item.
    * the item object that is going to be active.
    * @param {string} imageId selected image Id from thumbnail/filmstrip.
    * @returns MarqueeGalleryItem object
    */
    function getCollectionItem(imageId, collectionCategory, getGalleryCollection) {
      const state = builderShowcaseObserver.getState();
      const galleryCollection = getGalleryCollection(state, collectionCategory);
      const items = galleryCollection.Items;
      const item = items.find((x) => +x.Id === +imageId);

      return item;
    }

    /**
    * changeStyleWithFastDom: applies style changes to a given element withing a fastdom frame
    */
    function changeStyleWithFastDom(element) {
      element.parentElement.classList.remove('figure--active');
      element.removeAttribute('data-active');
    }

    /**
    * performStyleChange: calls a named function for changing styles using fast dom
    */
    function performStyleChange(element) {
      fastdom.mutate(changeStyleWithFastDom.bind(null, element));
    }

    /**
   * Removes the classes and attributes set to the last active image
   */
    function cleanActiveElements(activeThumbSelector) {
      const elements = document.querySelectorAll(activeThumbSelector);
      if (elements) {
        elements.forEach(performStyleChange);
      }
    }

    /**
   * Sets classes and attributes to a given picture.
   * @param {object} selectedThumbnail Picture HTML object
   */
    function applyActiveStyles(selectedThumbnail, activeThumbSelector) {
      cleanActiveElements(activeThumbSelector);

      fastdom.mutate(() => {
        selectedThumbnail.parentElement.classList.add('figure--active');
        selectedThumbnail.setAttribute('data-active', true);
      });
    }

    /**
    * @private
    * getMapUrl: gets url to hit the server in ajax requests related to map functionality.
    * Basically, it checks if we are querying from a private label to add the private
    * label to the url structure.
    *
    * @returns the url that should be requested for map related actions like getting
    * a single pin or a set of them.
    */
    function getMapUrl(url) {
      const siteRoot = document.querySelector('#nhs_siteRootLogger');
      return `${window.location.origin}${siteRoot.value}${url}`;
    }

    return {
      getImageType,
      setUpPlanDescription,
      calculateHaversineFormula,
      onMapUpdatedSuccess,
      getCollectionItem,
      applyActiveStyles,
      getMapUrl,
    };
  }
);

